import { useMemo } from 'react'
import { Subtitle, Title } from '@app/components/core/typography/Title'
import { PageLayout } from '@app/pages/layout/PageLayout'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { useTranslations } from '@i18n/useLanguage'
import { Text } from '@app/components/core/typography/Text'
import { Head, useQuery } from '@based/sdk/react'
import useMediaQuery from '@app/util/useMediaQuery'
import { PageBreadcrumbs } from '@app/components/blocks/pageBreadcrumbs/PageBreadcrumbs'
import { TabContent } from './sections/TabContent'
import { RightColumn } from './sections/RightColumn'
import { PageMediaGallery } from '@app/components/blocks/mediaGallery/PageMediaGallery'
import { DataCard } from './sections/DataCard'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'
import useTransform from '@app/util/useTransform'
import { ViewInitiativeModel } from 'functions/app/initiative/view'
import { isUuid } from '@app/util/helpers'

export type InitiativePageProps = {
  initiativeId: string
}

export const InitiativePage = ({ initiativeId }: InitiativePageProps) => {
  const isMobile = useMediaQuery()
  const translations = useTranslations().app
  const transformStyle = useTransform()

  const queryPayload = useMemo(() => {
    return isUuid(initiativeId) ? { id: initiativeId } : { slug: initiativeId }
  }, [initiativeId])

  const initiativeQuery = useQuery('view-initiative', queryPayload)
  const initiative: ViewInitiativeModel | null = initiativeQuery.data

  if (!initiative) {
    return (
      <PageLayout gap="2rem">
        <Column
          gap="1.5rem"
          alignItems="stretch"
          style={{ marginBottom: '90vh' }}
        >
          <PageBreadcrumbs
            level2={translations.pageNames.initiatives}
            level3="Loading..."
          />
        </Column>
      </PageLayout>
    )
  }

  if (isMobile) {
    return (
      <PageLayout gap="0rem">
        <Column alignItems="stretch" gap="1.5rem">
          <Column>
            <Title.H3>{initiative.title}</Title.H3>
            <Text.Medium>{initiative.description}</Text.Medium>
          </Column>
          <PageMediaGallery
            title={initiative.title}
            images={initiative.images}
            defaultImage={defaultProjectPicture}
          />
          <DataCard initiative={initiative} />
        </Column>
        <TabContent initiative={initiative} />
      </PageLayout>
    )
  }

  return (
    <PageLayout gap="3.5rem" standardPageTitle={false}>
      <Head>
        <title>{`${initiative.title} - CrowdBuilding`}</title>
        <meta name="description" content={initiative.description} />
        {initiative.images?.length > 0 && (
          <meta property="og:image" content={initiative.images[0].src} />
        )}
      </Head>
      <Column gap="2rem" alignItems="stretch" style={transformStyle}>
        <PageBreadcrumbs
          level2={translations.pageNames.initiatives}
          level3={initiative.title}
        />
        <Title.H1 style={{ paddingRight: '24rem' }}>
          {initiative.title}
        </Title.H1>
        <Subtitle style={{ paddingRight: '24rem' }}>
          {initiative.description}
        </Subtitle>
        <Row
          gap="1.5rem"
          style={{
            alignItems: 'flex-start',
            ...transformStyle,
          }}
        >
          <Column alignItems="stretch" gap="2rem" style={{ flex: '2 1 62rem' }}>
            <PageMediaGallery
              title={initiative.title}
              images={initiative.images}
              defaultImage={defaultProjectPicture}
              style={{ width: '100%' }}
            />
            <TabContent initiative={initiative} />
          </Column>
          <RightColumn initiative={initiative} />
        </Row>
      </Column>
    </PageLayout>
  )
}
